import { useEffect, useRef } from 'react';
import { useInfiniteQuery, type InfiniteData, type QueryKey } from '@tanstack/react-query';
import type { Page } from '@dashboard/contracts';
import { defaultQueryOptions } from './queries';

/**
 * Reading a keyset-paginated list all the way to the end.
 *
 * Every list endpoint in this API returns `{ items, nextCursor }` and caps the
 * page at a limit the caller cannot exceed. The query layer used to request one
 * page and drop `nextCursor` on the floor, which made every list in the product
 * silently finite: a register of sixty risks showed fifty, a project with more
 * than two hundred tasks drew a Gantt missing the rest, and two panels rendered
 * the word "truncated" with no way to act on it. Nothing was slow -- the data
 * was simply unreachable.
 *
 * There are two honest answers to that, and which one applies is a property of
 * the *component*, not of the endpoint:
 *
 *  - A list somebody reads top-down is finished by a **Load more** control.
 *    They see the first page immediately and pay for the rest only if they want
 *    it. {@link usePagedQuery} is that case, and it is the default.
 *
 *  - A list whose consumer needs the whole set before it is correct at all --
 *    a Gantt chart laying out a hierarchy, a dependency graph, a picker
 *    resolving names -- cannot show a partial answer without being wrong.
 *    {@link useCompleteQuery} fetches every page for those, one after another.
 *
 * Both are the same `useInfiniteQuery` underneath, so a component can be moved
 * from one to the other without its data shape changing.
 */

/** What both hooks return, flattened so callers never touch `data.pages`. */
export interface PagedResult<T> {
  /** Every item fetched so far, in page order. */
  items: T[];
  /** True until the first page has arrived. */
  isPending: boolean;
  error: unknown;
  refetch: () => void;
  /** Another page exists and has not been requested yet. */
  hasMore: boolean;
  /** A `fetchMore` is in flight. Distinct from `isPending`, which is the
   *  first load: the difference is a spinner on a button versus a skeleton
   *  where the list should be. */
  isLoadingMore: boolean;
  fetchMore: () => void;
  /** How many pages have arrived. Used by the exhaustive hook's safety bound. */
  pageCount: number;
}

/**
 * The most pages either hook will request for one list.
 *
 * A bound rather than a promise that the loop terminates: `nextCursor` comes
 * from the server, and a bug there -- a cursor that does not advance -- would
 * otherwise be an infinite request loop in every reader's browser rather than a
 * list that stops early. At the limits these endpoints allow (200 for the
 * register and the schedule, 100 elsewhere) this is tens of thousands of rows,
 * which is far past the point where a component should be paging at all.
 */
const MAX_PAGES = 50;

type QueryFn<T> = (context: { signal: AbortSignal; cursor?: string }) => Promise<Page<T>>;

/**
 * The subset of query options a paginated caller may set.
 *
 * Deliberately narrow rather than the whole `UseInfiniteQueryOptions` surface:
 * `queryFn`, `getNextPageParam` and `initialPageParam` are this module's job,
 * and a caller that could override them could break the cursor protocol from
 * the outside.
 */
interface SharedOptions {
  enabled?: boolean;
  staleTime?: number;
  refetchInterval?: number;
}

/** The page cursor. `undefined` is the first page -- the server assigns the rest. */
type PageParam = string | undefined;

function useCursorQuery<T>(
  queryKey: QueryKey,
  queryFn: QueryFn<T>,
  options: SharedOptions = {},
): PagedResult<T> & { fetchNextPage: () => void } {
  const query = useInfiniteQuery<
    Page<T>,
    Error,
    InfiniteData<Page<T>, PageParam>,
    QueryKey,
    PageParam
  >({
    queryKey,
    queryFn: ({ signal, pageParam }) => queryFn({ signal, cursor: pageParam }),
    initialPageParam: undefined,
    /* Returning `undefined` is what tells React Query the list is complete, so
     * `hasNextPage` and the exhaustive hook's stopping condition are both
     * derived from the server's own answer rather than from a count kept here. */
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    ...defaultQueryOptions,
    ...options,
  });

  const pages = query.data?.pages ?? [];

  return {
    items: pages.flatMap((page) => page.items),
    isPending: query.isPending,
    error: query.error,
    refetch: () => void query.refetch(),
    hasMore: query.hasNextPage,
    isLoadingMore: query.isFetchingNextPage,
    fetchMore: () => void query.fetchNextPage(),
    fetchNextPage: () => void query.fetchNextPage(),
    pageCount: pages.length,
  };
}

/**
 * A list the reader extends themselves, one page at a time.
 *
 * Pair it with `<LoadMore>`, which renders nothing when `hasMore` is false --
 * so a list that fits on one page looks exactly as it did before this existed.
 */
export function usePagedQuery<T>(
  queryKey: QueryKey,
  queryFn: QueryFn<T>,
  options: SharedOptions = {},
): PagedResult<T> {
  return useCursorQuery(queryKey, queryFn, options);
}

/**
 * A list fetched to completion, because a partial one would be wrong.
 *
 * The pages are requested one after another rather than in parallel -- a keyset
 * cursor names a position, so page three cannot be asked for until page two has
 * answered. In exchange each page is a bounded, indexed query, which is the
 * property that made keyset pagination worth having in the first place: the
 * fiftieth page costs what the first one did.
 *
 * `items` grows as pages arrive, so a caller that renders while this is running
 * shows a list filling in rather than a spinner. `isComplete` is there for the
 * callers that must not draw a half-answer.
 */
export function useCompleteQuery<T>(
  queryKey: QueryKey,
  queryFn: QueryFn<T>,
  options: SharedOptions = {},
): PagedResult<T> & { isComplete: boolean } {
  const result = useCursorQuery(queryKey, queryFn, options);
  const { hasMore, isLoadingMore, isPending, pageCount } = result;

  /*
   * The fetcher is reached through a ref rather than named as a dependency.
   *
   * `useCursorQuery` returns a fresh closure over `query.fetchNextPage` on
   * every render, so depending on it directly would re-run the paging effect on
   * every render -- and in the window between asking for a page and
   * `isFetchingNextPage` turning true, that is a burst of duplicate requests
   * rather than one. Whether another page is wanted is the only thing that
   * should start one.
   *
   * Updated in its own effect rather than during render: a ref written while
   * rendering is not safe under concurrent rendering, and React's lint rule is
   * right to refuse it. Declared before the effect below so it is committed
   * first.
   */
  const latestFetch = useRef(result.fetchNextPage);
  useEffect(() => {
    latestFetch.current = result.fetchNextPage;
  });

  const shouldFetchMore = hasMore && !isLoadingMore && !isPending && pageCount < MAX_PAGES;

  useEffect(() => {
    // `shouldFetchMore` flipping back to true after a page lands is what drives
    // the next iteration; it goes false for good when the server stops
    // returning a cursor.
    if (shouldFetchMore) latestFetch.current();
  }, [shouldFetchMore]);

  /*
   * "This hook will fetch nothing further", which is not quite the same as "the
   * list is complete": hitting `MAX_PAGES` also stops it. Reporting only
   * `!hasMore` would leave a caller showing "still loading" forever in the case
   * the bound exists to contain, which is a worse failure than a short list.
   * `hasMore` still being true beside `isComplete` is how a caller can tell the
   * two apart.
   */
  return { ...result, isComplete: !isPending && (!hasMore || pageCount >= MAX_PAGES) };
}

/**
 * One page, in the shape the cache holds for these hooks.
 *
 * For seeding `QueryClient.setQueryData` in a test. Both hooks are infinite
 * queries, so the cache entry is `{ pages, pageParams }` rather than the `Page`
 * the endpoint returns -- and a test that writes the wrong one does not fail
 * with a type error, it fails inside React Query's paging internals with a
 * message about `undefined`. Exported so that shape is stated in one place
 * instead of copied into every test that seeds a list.
 */
export function seedPage<T>(items: T[], nextCursor: string | null = null): InfiniteData<Page<T>> {
  return { pages: [{ items, nextCursor }], pageParams: [undefined] };
}
